import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Users, X } from "lucide-react";
import { CATEGORY_LABELS, CATEGORY_ICONS } from "./constants";

const SECTION_IDS = {
  financing: "local-mortgage",
  real_estate_agent: "local-agents",
  inspections: "local-services",
  cleaning: "local-services",
  moving: "local-services",
  junk_removal: "local-services",
};

/** Persistent floating button shown on every Property Website page. Lets a
 * visitor jump straight to a category's section on the Home page, or go
 * straight to a specific advertiser's own profile page. */
export function LocalExpertsButton({ slug, sponsorsByCategory = {}, onSponsorsPage = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onClick = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const categories = Object.keys(CATEGORY_LABELS).filter((cat) => (sponsorsByCategory[cat]?.length || 0) > 0);
  if (!categories.length) return null;

  const goToSection = (cat) => {
    setOpen(false);
    if (onSponsorsPage) {
      document.getElementById(`sponsors-${cat}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const anchor = SECTION_IDS[cat] || "local-services";
    const onHome = location.pathname === `/listing/${slug}` || location.pathname === `/listing/${slug}/`;
    if (onHome) {
      document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/listing/${slug}#${anchor}`);
    }
  };

  return (
    <div ref={ref} className="fixed bottom-24 sm:bottom-5 right-5 z-40" data-testid="local-experts-widget">
      {open && (
        <div data-testid="local-experts-menu" className="absolute bottom-16 right-0 w-72 rounded-2xl border border-[#E7E4DE] bg-white shadow-xl p-2 max-h-[70vh] overflow-y-auto">
          <div className="px-3 py-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#667085]">Local Experts</p>
            <button data-testid="local-experts-menu-close" onClick={() => setOpen(false)} className="text-[#667085] hover:text-[#202833]"><X size={14} /></button>
          </div>
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat];
            const ads = sponsorsByCategory[cat] || [];
            return (
              <div key={cat} className="mb-1">
                <button
                  data-testid={`local-experts-category-${cat}`}
                  onClick={() => goToSection(cat)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-[#F8F7F4] transition text-left"
                >
                  {Icon && <Icon size={15} className="text-[#C7A361] flex-shrink-0" />}
                  <span className="text-sm text-[#202833] font-medium">{CATEGORY_LABELS[cat]}</span>
                  <span className="ml-auto text-[11px] text-[#98A2B3]">{ads.length}</span>
                </button>
                {ads.length <= 3 && (
                  <div className="pl-9">
                    {ads.map((ad) => (
                      <Link key={ad.id} to={`/local-experts/${ad.slug}`} onClick={() => setOpen(false)} data-testid={`local-experts-advertiser-${ad.id}`} className="block text-xs text-[#667085] hover:text-[#C7A361] py-1 truncate">{ad.business_name}</Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <button
        data-testid="local-experts-toggle"
        onClick={() => setOpen((v) => !v)}
        className="h-14 w-14 rounded-full bg-[#202833] text-white shadow-lg grid place-items-center hover:bg-[#111820] transition-colors"
        aria-label="Local Experts"
      >
        {open ? <X size={20} /> : <Users size={20} />}
      </button>
    </div>
  );
}
